import React from 'react';
import { PiPlus } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  WorkflowInputEditorProps,
  WorkflowInputField,
} from './workflow-input-editor.types';
import { WorkflowFieldCard } from './workflow-input-field-card';
import { useWorkflowInputEditor } from './workflow-input-editor.hooks';

export const WorkflowInputEditor: React.FC<WorkflowInputEditorProps> = ({
  value,
  onChange,
  onValidationChange,
}) => {
  const {
    fields,
    errors,
    fieldErrors,
    enumDrafts,
    setEnumDraft,
    addField,
    updateField,
    removeField,
    addSubField,
    updateSubField,
    removeSubField,
    addEnumValue,
    removeEnumValue,
  } = useWorkflowInputEditor({ value, onChange, onValidationChange });

  const renderFieldCard = (field: WorkflowInputField) => (
    <WorkflowFieldCard
      key={field.id}
      field={field}
      enumDrafts={enumDrafts}
      fieldErrorsMap={fieldErrors}
      onEnumDraftChange={setEnumDraft}
      onAddEnumValue={addEnumValue}
      onRemoveEnumValue={removeEnumValue}
      onUpdateField={(patch) => updateField(field.id, patch)}
      onRemoveField={() => removeField(field.id)}
      onAddSubField={() => addSubField(field.id)}
      onUpdateSubField={(subId, patch) =>
        updateSubField(field.id, subId, patch)
      }
      onRemoveSubField={(subId) => removeSubField(field.id, subId)}
    />
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-col">
          <Text as="h4" size="small" weight="medium" className="text-slate-700">
            Entradas del flujo
          </Text>
          <Text size="tiny" className="text-slate-500">
            Define los campos de entrada disponibles para este flujo.
          </Text>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {fields.length}/20 campos
          </span>
          <Button variant="primary" size="sm" onClick={addField}>
            <div className="flex items-center gap-2">
              <PiPlus className="w-4 h-4" />
              <span>Agregar campo</span>
            </div>
          </Button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded p-3 space-y-1">
          {errors.map((err, idx) => (
            <div key={`${err}-${idx}`} className="text-sm">
              {err}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {fields.length === 0 ? (
          <EmptyState
            description="Agrega campos para definir qué datos recibe este flujo."
            appearance="soft"
            variant="neutral"
            size="sm"
            className="py-6"
          />
        ) : (
          fields.map((field) => renderFieldCard(field))
        )}
      </div>
    </div>
  );
};

export default WorkflowInputEditor;
